
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Copy } from "lucide-react";

export const WebhookConfig = () => {
  const { toast } = useToast();
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWebhookUrl();
  }, []);

  const loadWebhookUrl = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get the webhook signing key for the user
      const { data: webhookData, error: webhookError } = await supabase
        .from('webhook_events')
        .select('signing_key')
        .eq('user_id', user.id)
        .limit(1)
        .single();

      if (webhookError && webhookError.code !== 'PGRST116') {
        throw webhookError;
      }

      if (webhookData?.signing_key) {
        const url = `${window.location.origin}/api/webhook/${user.id}?key=${webhookData.signing_key}`;
        setWebhookUrl(url);
      } else {
        // Create a new webhook signing key if none exists
        const signingKey = crypto.randomUUID();
        const { error: insertError } = await supabase
          .from('webhook_events')
          .insert({
            user_id: user.id,
            signing_key: signingKey,
            phone_number: '', // Required field, but we'll update it later
            event_type: 'message.phone.received' // Default event type
          });

        if (insertError) throw insertError;

        const url = `${window.location.origin}/api/webhook/${user.id}?key=${signingKey}`;
        setWebhookUrl(url);
      }
    } catch (error) {
      console.error('Error loading webhook URL:', error);
      toast({
        title: "Error loading webhook configuration",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyWebhook = () => {
    if (webhookUrl) {
      navigator.clipboard.writeText(webhookUrl);
      toast({
        title: "Webhook URL copied",
        description: "The webhook URL has been copied to your clipboard",
      });
    }
  };

  return (
    <div className="space-y-4">
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Your webhook URL is unique and should be kept secure. Events will be signed with your key for verification.
        </AlertDescription>
      </Alert>
      
      {loading ? (
        <div className="h-10 bg-gray-100 animate-pulse rounded" />
      ) : webhookUrl ? (
        <div className="space-y-2">
          <div className="bg-gray-50 p-4 rounded-lg break-all font-mono text-sm">
            {webhookUrl}
          </div>
          <Button 
            variant="outline"
            onClick={handleCopyWebhook}
            className="w-full flex items-center justify-center gap-2"
          >
            <Copy className="h-4 w-4" />
            Copy Webhook URL
          </Button>
        </div>
      ) : (
        <div className="text-gray-500 text-center py-4">
          No webhook URL configured
        </div>
      )}
    </div>
  );
};
